import { Fragment, cloneElement, isValidElement, useState, useCallback, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { SearchOutlined } from '@ant-design/icons';
import { Button, Checkbox, Input, Radio, Space } from 'antd';

// ─────────────────────────────────────────────
// 1. UI Components
// ─────────────────────────────────────────────

/**
 * Багана гарчгийн доор байрлах, шууд харагдах хайлтын Input.
 * Icon дарах шаардлагагүй — Enter дарахад (эсвэл цэвэрлэхэд) onSearch дуудна.
 */
const SearchHeader = ({ label, placeholder, value, onSearch }) => {
  const [text, setText] = useState(value ?? '');

  // URL дахь утга өөрчлөгдвөл (жишээ нь буцаж ирэх, цэвэрлэх) input-г нийцүүлнэ
  useEffect(() => {
    setText(value ?? '');
  }, [value]);

  return (
    // Гарчиг дээр дарахад sort/dropdown ажиллуулахгүйн тулд propagation зогсооно
    <div onClick={(e) => e.stopPropagation()}>
      <div className="pp-drag-handle" style={{ marginBottom: 5, fontWeight: 500 }}>
        {label}
      </div>
      <Input
        size="small"
        allowClear
        style={{ borderRadius: '10px' }}
        prefix={<SearchOutlined style={{ color: '#000' }} />}
        placeholder={placeholder || 'Хайх'}
        value={text}
        onChange={(e) => {
          const next = e.target.value;
          setText(next);
          // allowClear-ийн (×) товч эсвэл бүрэн устгахад filter-г шууд арилгана
          if (next === '') onSearch('');
        }}
        onPressEnter={() => onSearch(text.trim())}
        onKeyDown={(e) => e.stopPropagation()}
      />
    </div>
  );
};

/** Шүүлтүүрийн dropdown-ийн доод талын товчнуудын мөр */
const FilterFooter = ({ children }) => (
  <div
    style={{
      borderTop: '1px solid #f0f0f0',
      marginTop: 8,
      paddingTop: 6,
      textAlign: 'right',
    }}
  >
    {children}
  </div>
);

/**
 * Шүүлтийг ШУУД арилгах "Цэвэрлэх" товч.
 *
 * Ant Design-ийн үндсэн товч нь зөвхөн dropdown доторх сонголтыг арилгаад
 * шүүлтийг нь хэвээр үлдээдэг — "Тийм" дарж байж жинхэнэ утгаараа арилдаг.
 * Тиймээс хэрэглэгч шүүлт арилсан гэж бодоод шүүгдсэн жагсаалтыг бүтэн гэж
 * уншиж болзошгүй байсан. `confirm: true` нь шүүлтийг тэр дороо хэрэгжүүлнэ.
 */
const ClearFilterButton = ({ selectedKeys, clearFilters }) => (
  <Button
    type="link"
    size="small"
    disabled={!selectedKeys?.length}
    onClick={() => clearFilters({ confirm: true, closeDropdown: true })}
  >
    Цэвэрлэх
  </Button>
);

/**
 * Нэг сонголттой (radio) шүүлтүүрийн dropdown — сонгонгуут шүүлт шууд хэрэгжинэ.
 * Ant Design-ийн default dropdown нь "OK" товч дарахыг шаарддаг тул өөрсдөө зурав.
 */
const InstantFilterDropdown = ({
  filters = [],
  selectedKeys,
  setSelectedKeys,
  confirm,
  clearFilters,
}) => (
  <div style={{ padding: 8, minWidth: 160 }} onKeyDown={(e) => e.stopPropagation()}>
    <Radio.Group
      value={selectedKeys?.[0]}
      onChange={(e) => {
        // antd-ийн confirm() нь setSelectedKeys-ийн утгыг шууд (sync) уншдаг
        setSelectedKeys([e.target.value]);
        confirm();
      }}
    >
      <Space direction="vertical" size={4}>
        {filters.map(({ text, value }) => (
          <Radio key={value} value={value}>
            {text}
          </Radio>
        ))}
      </Space>
    </Radio.Group>
    <FilterFooter>
      <ClearFilterButton selectedKeys={selectedKeys} clearFilters={clearFilters} />
    </FilterFooter>
  </div>
);

/**
 * Олон сонголттой (checkbox) шүүлтүүрийн dropdown.
 *
 * Ant Design-ийн үндсэнтэй харагдац нэг ч, "Цэвэрлэх" нь шүүлтийг шууд арилгана
 * ({@link ClearFilterButton}-ийг үз). Хэд хэдэн утга сонгох боломжтой хэвээр
 * тул сонголтуудыг "Тийм"-ээр баталгаажуулна.
 */
const MultiFilterDropdown = ({
  filters = [],
  selectedKeys,
  setSelectedKeys,
  confirm,
  clearFilters,
}) => (
  <div style={{ padding: 8, minWidth: 160 }} onKeyDown={(e) => e.stopPropagation()}>
    {/* antd нь шүүлтүүрийн утгыг дотооддоо текст болгодог тул мөн адил харьцуулна */}
    <Checkbox.Group value={selectedKeys} onChange={setSelectedKeys}>
      <Space direction="vertical" size={4} style={{ maxHeight: 260, overflowY: 'auto' }}>
        {filters.map(({ text, value }) => (
          <Checkbox key={String(value)} value={String(value)}>
            {text}
          </Checkbox>
        ))}
      </Space>
    </Checkbox.Group>
    <FilterFooter>
      <Space size={8}>
        <ClearFilterButton selectedKeys={selectedKeys} clearFilters={clearFilters} />
        <Button type="primary" size="small" onClick={() => confirm()}>
          Тийм
        </Button>
      </Space>
    </FilterFooter>
  </div>
);

// ─────────────────────────────────────────────
// 2. Хайлтын үр дүнг тодотгох (Cmd+F маягийн highlight)
// ─────────────────────────────────────────────

/** Тодотгосон текстийн харагдац — browser-ийн Cmd+F-тэй адил шар өнгө */
const HIGHLIGHT_STYLE = {
  backgroundColor: '#ffe58f',
  color: 'inherit',
  borderRadius: 2,
};

/** getColumnSearchProps багана дээр үлдээх хайлтын утга (withHighlight уншина) */
const SEARCH_VALUE_KEY = '__ppSearchValue';

/** RegExp-д тусгай утгатай тэмдэгтүүдийг escape хийнэ */
const escapeRegExp = (text) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Текст доторх таарсан хэсгүүдийг <mark>-аар боож өгнө (том/жижиг үсэг ялгахгүй) */
const highlightText = (text, keyword) => {
  // Capture group-той split: сондгой индекс бүр нь таарсан хэсэг
  const parts = String(text).split(new RegExp(`(${escapeRegExp(keyword)})`, 'gi'));
  if (parts.length === 1) return text;

  return parts.map((part, index) =>
    index % 2 === 1 ? (
      <mark key={index} style={HIGHLIGHT_STYLE}>
        {part}
      </mark>
    ) : (
      part
    )
  );
};

/**
 * Нүдний доторх ямар ч агуулгыг (текст, массив, Tag/Link зэрэг элемент)
 * рекурсиваар гүйж, таарсан текстийг тодотгоно.
 */
const highlightNode = (node, keyword) => {
  if (typeof node === 'string' || typeof node === 'number') return highlightText(node, keyword);

  if (Array.isArray(node)) {
    return node.map((child, index) => (
      <Fragment key={index}>{highlightNode(child, keyword)}</Fragment>
    ));
  }

  // Элемент бол child-уудыг нь гүйнэ. Function child (render props) бол хөндөхгүй.
  if (isValidElement(node)) {
    const children = node.props?.children;
    if (children === undefined || typeof children === 'function') return node;
    return cloneElement(node, undefined, highlightNode(children, keyword));
  }

  return node;
};

/** Нэг баганыг тодотгодог болгоно (бүлэглэсэн багана бол дотоод багануудыг нь ч мөн) */
const highlightColumn = (column) => {
  if (!column) return column;

  const { [SEARCH_VALUE_KEY]: keyword, ...rest } = column;
  const next = rest.children
    ? { ...rest, children: rest.children.map(highlightColumn) }
    : { ...rest };

  if (!keyword) return next;

  const baseRender = column.render;
  next.render = (value, record, index) => {
    const result = baseRender ? baseRender(value, record, index) : value;

    // Ant Design-ийн нүд нэгтгэх хэлбэр: { children, props }
    if (result && typeof result === 'object' && !isValidElement(result) && 'children' in result) {
      return { ...result, children: highlightNode(result.children, keyword) };
    }
    return highlightNode(result, keyword);
  };

  return next;
};

// ─────────────────────────────────────────────
// 3. Эрэмбэлэлт
// ─────────────────────────────────────────────

/**
 * URL query дахь шүүлтүүрийн бус (тусгай утгатай) түлхүүрүүд.
 * Эдгээр нь `filters` объектод орохгүй — fetchData руу тусдаа талбараар очно.
 */
const RESERVED_KEYS = ['page', 'pageSize', 'sort', 'order'];

/**
 * Ant Design-ийн `sorter` аргументаас эрэмбийг гаргана.
 *
 * Олон баганаар эрэмбэлэх үед массив ирдэг — бид нэг баганын эрэмбийг л дэмждэг тул
 * эхнийхийг нь авна. Гурав дахь товшилтод antd нь `order`-ыг `undefined` болгодог
 * бөгөөд энэ нь "эрэмбийг цуцлах" гэсэн үг.
 *
 * @returns `{ sort, order }` — цуцалсан бол хоёулаа `undefined`.
 */
const readSorter = (sorter) => {
  const single = Array.isArray(sorter) ? sorter[0] : sorter;

  if (!single?.order) return { sort: undefined, order: undefined };

  // `columnKey` нь багана дээр `key` өгсөн үед, эс бөгөөс `field` (dataIndex) ирнэ
  const key = single.columnKey ?? single.field;
  if (!key) return { sort: undefined, order: undefined };

  return { sort: String(key), order: single.order };
};

// ─────────────────────────────────────────────
// 4. Hook — Server-side filter (URL query-д хадгална)
// ─────────────────────────────────────────────

/**
 * Table-ийн багана дээр хайлт хийж, утгыг list service рүү илгээх hook.
 *
 * Filter/pagination утгыг URL query string-д хадгалдаг тул дэлгэрэнгүй рүү
 * ороод буцаж ирэхэд (location.search сэргэдэг) хайлт нь хэвээр хадгалагдана.
 *
 * Ашиглах:
 *   const { getColumnSearchProps, getColumnFilterProps, getColumnSortProps,
 *           handleTableChange, withHighlight } = useTableFilter({ fetchData });
 *
 *   const columns = withHighlight([
 *     // Текстээр хайх багана — гарчгийн доор шууд харагдах Input
 *     { dataIndex: "code", ...getColumnSearchProps("code", "Захиалгын дугаар") },
 *     // Enum-ээр шүүх багана — Ant Design-ийн dropdown filter
 *     { dataIndex: "orderStatus", filters: [...], ...getColumnFilterProps("orderStatus") },
 *     // Сервер талд эрэмбэлэх багана
 *     { dataIndex: "createdAt", ...getColumnSortProps("createdAt") },
 *   ]);
 *
 *   <Table columns={columns} onChange={handleTableChange} />
 *
 * withHighlight нь хайлт хийсэн баганын нүд дэх таарсан текстийг Cmd+F шиг
 * шараар тодотгоно. Хэрэглэхгүй бол зүгээр columns-оо шууд өгч болно.
 *
 * fetchData нь дараах хэлбэртэй байх ёстой:
 *   const fetchData = async ({ page, pageSize, sort, order, ...filters } = {}) => { ... }
 *
 * `sort`/`order` нь `filters`-т ХОЛИЛДОХГҮЙ, тусдаа талбараар очно — API бүр
 * эрэмбийн параметрээ өөр нэрлэдэг (`sort`/`order`, `sortField`/`sortOrder` г.м.)
 * тул дэлгэц өөрөө буулгаж өгнө.
 *
 * Анхаар: энэ hook нь өөрөө fetchData-г дуудна. Тиймээс view дотор
 * useEffect(() => fetchData(), []) гэж давхар дуудах шаардлагагүй.
 */
export const useTableFilter = ({ fetchData }) => {
  const history = useHistory();
  const location = useLocation();

  // URL query string-ээс одоогийн filter, эрэмбэ болон pagination-г уншина.
  // Нэг түлхүүр олон утгатай байж болно: ?orderStatus=created&orderStatus=canceled
  // => 1 утга бол string, олон бол массив болгож хадгална.
  const params = new URLSearchParams(location.search);
  const page = Number(params.get('page')) || 1;
  const pageSize = Number(params.get('pageSize')) || 10;
  const sort = params.get('sort') ?? undefined;
  const order = params.get('order') ?? undefined;
  const filters = {};
  params.forEach((_, key) => {
    if (RESERVED_KEYS.includes(key)) return;
    const values = params.getAll(key);
    filters[key] = values.length === 1 ? values[0] : values;
  });

  // location.search өөрчлөгдөх бүрд (хайлт хийх, эсвэл буцаж ирэх) дахин дуудна
  useEffect(() => {
    fetchData({ page, pageSize, sort, order, ...filters });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Өгсөн утгуудыг URL query-д нэгтгэнэ. Хоосон утга бол түлхүүрийг устгана
  // (ингэснээр filter-ээ цэвэрлэхэд URL-аас алга болно). Массив утгыг олон
  // мөрөөр (append) хадгална.
  const updateParams = useCallback(
    (next) => {
      const merged = new URLSearchParams(location.search);
      Object.entries(next).forEach(([key, value]) => {
        merged.delete(key);
        const isEmpty =
          value === undefined ||
          value === null ||
          value === '' ||
          (Array.isArray(value) && value.length === 0);
        if (isEmpty) return;
        if (Array.isArray(value)) value.forEach((v) => merged.append(key, v));
        else merged.set(key, value);
      });
      // Утга өөрчлөгдөөгүй бол дэмий refetch хийхгүй
      const nextSearch = merged.toString();
      if (nextSearch === new URLSearchParams(location.search).toString()) return;
      history.push({ search: nextSearch });
    },
    [history, location.search]
  );

  const handleTableChange = useCallback(
    (pagination, tableFilters, sorter, extra) => {
      // Ant Design filter массив хэлбэртэй ирдэг: { orderStatus: ["created", ...] }
      // null/хоосон бол undefined болгож updateParams-д устгуулна
      const active = Object.fromEntries(
        Object.entries(tableFilters).map(([key, value]) => [key, value?.length ? value : undefined])
      );

      // Эрэмбийг ЗӨВХӨН эрэмбэлэх үйлдэл дээр хөндөнө. Шүүх/хуудаслах үед antd нь
      // `sorter`-ыг `order: undefined`-тэй буцаадаг тул болгон дээр нь бичвэл
      // өмнөх эрэмбэ URL-аас арчигдана. (Цуцлах нь ч мөн `action: "sort"` байдаг.)
      const isSorting = extra?.action === 'sort';
      const nextSort = isSorting ? readSorter(sorter) : {};

      updateParams({
        // Filter эсвэл эрэмбэ солиход эхний хуудас руу буцаана
        page: ['filter', 'sort'].includes(extra?.action) ? 1 : pagination.current,
        pageSize: pagination.pageSize,
        ...nextSort,
        ...active,
      });
    },
    [updateParams]
  );

  // filteredValue-г Ant Design-д массив хэлбэрээр өгнө (string ч, массив ч байсан)
  const toFilteredValue = (value) =>
    value == null || value.length === 0 ? null : [].concat(value);

  // Текстээр хайх багана — гарчгийн доор шууд харагдах Input.
  // 2 дахь аргумент нь гарчгийн текст ба input-ийн placeholder болно.
  const getColumnSearchProps = useCallback(
    (dataIndex, placeholder) => ({
      title: (
        <SearchHeader
          label={placeholder}
          placeholder={placeholder}
          value={filters[dataIndex] ?? ''}
          onSearch={(value) => updateParams({ [dataIndex]: value || undefined, page: 1 })}
        />
      ),
      // withHighlight энэ утгаар нүдний текстийг тодотгоно
      [SEARCH_VALUE_KEY]: [].concat(filters[dataIndex] ?? '')[0] || '',
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [location.search, updateParams]
  );

  // Enum/сонголтоор шүүх багана (Төлөв гэх мэт).
  // `filters` жагсаалтыг багана дээрээ inline тодорхойлж, энэ props-г нэмнэ:
  //   { dataIndex: "orderStatus", filters: [...], ...getColumnFilterProps("orderStatus") }
  // default нь олон сонголт (multiple). Цэвэрлэхэд URL-аас автоматаар устана.
  // multiple: false бол сонголт дарангуут хүсэлт шууд явна (OK товч дарахгүй).
  const getColumnFilterProps = useCallback(
    (dataIndex, { multiple = true } = {}) => ({
      filterMultiple: multiple,
      filteredValue: toFilteredValue(filters[dataIndex]),
      filterDropdown: (props) =>
        multiple ? <MultiFilterDropdown {...props} /> : <InstantFilterDropdown {...props} />,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [location.search]
  );

  // Эрэмбэлэх багана. Эрэмбийг серверт хийдэг тул antd-д зөвхөн `sorter: true` гэж
  // хэлээд (клиент талд эрэмбэлүүлэхгүй), одоогийн чиглэлийг `sortOrder`-оор өгнө —
  // ингэснээр refresh хийсний дараа ч сум байрандаа харагдана.
  //   { dataIndex: "code", ...getColumnSortProps("code") }
  // Анхаар: багана дээр `key` өгсөн бол dataIndex биш ТҮҮНИЙГ дамжуулна
  // (antd нь `columnKey`-г эрэмбийн түлхүүр болгож буцаадаг).
  const getColumnSortProps = useCallback(
    (dataIndex) => ({
      sorter: true,
      sortOrder: sort === dataIndex ? order : null,
    }),
    [sort, order]
  );

  // Багануудыг тодотгодог болгож буцаана. Багана бүрийн өөрийнх нь render-ийг
  // хадгалж, гаралт дээр нь л highlight хийдэг тул Tag/Link зэрэг харагдац эвдрэхгүй.
  //   const columns = withHighlight(getColumns(...));
  const withHighlight = useCallback((columns = []) => columns.map(highlightColumn), []);

  return {
    getColumnSearchProps,
    getColumnFilterProps,
    getColumnSortProps,
    handleTableChange,
    withHighlight,
    filters,
    sort,
    order,
  };
};
